<template>
  <div class="gc-bom-bulk-update-step-preview">
    <!-- Loading state -->
    <div v-if="computing" class="pvw-computing">
      <div class="pvw-computing-inner">
        <div class="pvw-spinner"></div>
        <span>Computing changes...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="computeError" class="pvw-error-state">
      <span class="pvw-error-msg">{{ computeError }}</span>
      <DxButton
        text="Retry"
        type="normal"
        styling-mode="outlined"
        @click="computeDiff"
      />
    </div>

    <!-- Content -->
    <div v-else-if="diffBoms.length > 0" class="pvw-layout">
      <!-- Main column -->
      <div class="pvw-main">
        <div class="pvw-card">
          <div class="pvw-card-header">
            <span class="pvw-card-title">Change preview — per BOM</span>
          </div>
          <div class="pvw-card-body">
            <!-- Execution order note -->
            <div v-if="hasBothOps" class="pvw-exec-note">
              ℹ Process changes will be applied first, then material changes.
            </div>

            <!-- Filter pills -->
            <div class="pvw-fpills">
              <div
                v-for="pill in filterPills"
                :key="pill.key"
                :class="['pvw-fpill', { active: activeFilter === pill.key }]"
                @click="activeFilter = pill.key"
              >
                {{ pill.label }} ({{ pill.count }})
              </div>
            </div>

            <!-- Colour legend -->
            <div class="pvw-legend">
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-amber"></span>Qty / run rate
                changed
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-green"></span>Line added
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-red"></span>Removed
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-leg-dot pvw-dot-grey"></span>No change
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-type-badge pvw-badge-mat">Mat</span>Material
                line
              </div>
              <div class="pvw-leg-item">
                <span class="pvw-type-badge pvw-badge-proc">Proc</span>Process
                line
              </div>
            </div>

            <!-- BOM diff panels -->
            <div class="pvw-diff-list">
              <GcBomBulkUpdateBomDiffPanel
                v-for="bom in filteredBoms"
                :key="bom.bomCode"
                :bom="bom"
                :is-excluded="isExcluded(bom.bomCode)"
                :is-read-only="isReadOnly"
                :line-filter="activePanelLineFilter"
                @exclude="onExcludeBom"
                @include="onIncludeBom"
              />

              <div v-if="filteredBoms.length === 0" class="pvw-empty">
                No BOMs match this filter.
              </div>
            </div>

            <div class="pvw-count-footer">
              Showing {{ filteredBoms.length }} of {{ diffBoms.length }} BOMs
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar — Preview Summary -->
      <div class="pvw-sidebar">
        <div class="pvw-sum-card">
          <div class="pvw-sum-header">
            <span class="pvw-sum-title">Preview summary</span>
          </div>
          <div class="pvw-sum-body">
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">BOMs included</span>
              <span class="pvw-sum-val pvw-sv-blue">{{
                summaryStats.included
              }}</span>
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">BOMs excluded</span>
              <span
                :class="[
                  'pvw-sum-val',
                  summaryStats.excluded > 0 ? 'pvw-sv-red' : '',
                ]"
                >{{ summaryStats.excluded }}</span
              >
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Mat lines changed</span>
              <span class="pvw-sum-val pvw-sv-amber">{{
                summaryStats.matChanged
              }}</span>
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Proc lines changed</span>
              <span class="pvw-sum-val pvw-sv-amber">{{
                summaryStats.procChanged
              }}</span>
            </div>
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Lines added</span>
              <span class="pvw-sum-val pvw-sv-green">{{
                summaryStats.added
              }}</span>
            </div>
            <!-- Replace and Added are now both green as they represent additions; Replaced is not a removal in the current logic -->
            <!-- <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Lines replaced</span>
              <span
                :class="[
                  'pvw-sum-val',
                  summaryStats.replaced > 0 ? 'pvw-sv-red' : '',
                ]"
                >{{ summaryStats.replaced }}</span
              >
            </div> -->
            <div class="pvw-sum-row">
              <span class="pvw-sum-lbl">Lines removed</span>
              <span class="pvw-sum-val">{{ summaryStats.removed }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import { BomBulkUpdateService } from "@/_services/api/production/bom-bulk-update.service";
import GcBomBulkUpdateBomDiffPanel from "./gc-bom-bulk-update-bom-diff-panel.vue";

export default {
  name: "GcBomBulkUpdateStepPreview",
  components: { DxButton, GcBomBulkUpdateBomDiffPanel },
  props: {
    active: { type: Boolean, default: false },
    matchedBomList: { type: Array, default: () => [] },
    excludedBomIds: { type: Array, default: () => [] },
    materialUpdateConfig: { type: Object, default: null },
    processUpdateConfig: { type: Object, default: null },
    diffSnapshot: { type: [Array, Object], default: null },
    isReadOnly: { type: Boolean, default: false },
    searchCriteria: { type: Object, default: null },
    autoExcludedBomIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      computing: false,
      computeError: null,
      diffBoms: [],
      activeFilter: "all",
    };
  },
  computed: {
    hasBothOps() {
      return (
        this.materialUpdateConfig != null && this.processUpdateConfig != null
      );
    },
    filterPills() {
      const all = this.diffBoms;
      const excl = (b) => this.isExcluded(b.bomCode);
      const notExcl = (b) => !excl(b);
      const hasMat = (b) =>
        b.diffLines.some(
          (l) =>
            l.lineType === "Material" &&
            !["NoChange", "NotApplicable"].includes(l.changeType),
        );
      const hasProc = (b) =>
        b.diffLines.some(
          (l) =>
            l.lineType === "Process" &&
            !["NoChange", "NotApplicable"].includes(l.changeType),
        );
      const hasAdd = (b) => b.diffLines.some((l) => l.changeType === "Added");
      return [
        { key: "all", label: "All", count: all.length },
        {
          key: "mat",
          label: "Mat changes",
          count: all.filter((b) => notExcl(b) && hasMat(b)).length,
        },
        {
          key: "proc",
          label: "Proc changes",
          count: all.filter((b) => notExcl(b) && hasProc(b)).length,
        },
        {
          key: "additions",
          label: "Has additions",
          count: all.filter((b) => notExcl(b) && hasAdd(b)).length,
        },
        {
          key: "excluded",
          label: "Excluded",
          count: all.filter(excl).length,
        },
      ];
    },
    filteredBoms() {
      const excl = (b) => this.isExcluded(b.bomCode);
      switch (this.activeFilter) {
        case "mat":
          return this.diffBoms.filter(
            (b) =>
              !excl(b) &&
              b.diffLines.some(
                (l) =>
                  l.lineType === "Material" &&
                  !["NoChange", "NotApplicable"].includes(l.changeType),
              ),
          );
        case "proc":
          return this.diffBoms.filter(
            (b) =>
              !excl(b) &&
              b.diffLines.some(
                (l) =>
                  l.lineType === "Process" &&
                  !["NoChange", "NotApplicable"].includes(l.changeType),
              ),
          );
        case "additions":
          return this.diffBoms.filter(
            (b) =>
              !excl(b) && b.diffLines.some((l) => l.changeType === "Added"),
          );
        case "excluded":
          return this.diffBoms.filter(excl);
        default:
          return this.diffBoms;
      }
    },
    activePanelLineFilter() {
      if (this.activeFilter === "mat") return "Material";
      if (this.activeFilter === "proc") return "Process";
      return null;
    },
    summaryStats() {
      const included = this.diffBoms.filter((b) => !this.isExcluded(b.bomCode));
      const lines = included.flatMap((b) => b.diffLines);

      return {
        included: included.length,
        excluded: this.excludedBomIds.length,
        matChanged: lines.filter(
          (l) => l.lineType === "Material" && l.changeType === "QtyChanged",
        ).length,
        procChanged: lines.filter(
          (l) => l.lineType === "Process" && l.changeType === "RunRateChanged",
        ).length,
        added:
          lines.filter((l) => l.changeType === "Added").length +
          lines.filter((l) => l.changeType === "Replaced").length,
        // replaced: lines.filter((l) => l.changeType === "Replaced").length,
        removed: lines.filter((l) => l.changeType === "Removed").length,
      };
    },
  },
  watch: {
    active(val) {
      if (val && !this.diffBoms.length && !this.computing) {
        this.initDiff();
      }
    },
  },
  mounted() {
    if (this.active) {
      this.initDiff();
    }
  },
  methods: {
    // ─── Public API ────────────────────────────────────────────────────────────

    reset() {
      this.diffBoms = [];
      this.computeError = null;
      this.activeFilter = "all";
    },

    // ─── Diff lifecycle ────────────────────────────────────────────────────────

    async initDiff() {
      // Snapshot is the raw ComputeDiff response: { BomDiffs: { ... }, DiffComputedAt }
      if (this.diffSnapshot && this.diffSnapshot.BomDiffs) {
        this.diffBoms = this.normaliseDiff(this.diffSnapshot);
        this.handleZeroAffectedBoms();
      } else {
        await this.computeDiff();
      }
    },
    async computeDiff() {
      this.computing = true;
      this.computeError = null;
      try {
        const bomCodes = this.matchedBomList
          .map((b) => b.BomCode || b.bomCode)
          .filter((code) => !this.excludedBomIds.includes(code));

        const payload = {
          BomCodes: bomCodes,
          MaterialUpdateConfig: this.materialUpdateConfig || null,
          ProcessUpdateConfig: this.processUpdateConfig || null,
        };

        // MaterialSearchCriteria — UpdateQtyAll only
        var matConfig = this.materialUpdateConfig;
        var matCriteria = this.searchCriteria;

        if (
          matConfig &&
          matConfig.Operation === "UpdateQtyAll" &&
          matCriteria &&
          matCriteria.MaterialCriteria &&
          matCriteria.MaterialCriteria.length
        ) {
          payload.MaterialSearchCriteria = matCriteria.MaterialCriteria;
        }

        // ProcessSearchCriteria — UpdateRunRateAll only
        var procConfig = this.processUpdateConfig;
        var procCriteria = this.searchCriteria;

        if (
          procConfig &&
          procConfig.Operation === "UpdateRunRateAll" &&
          procCriteria &&
          procCriteria.ProcessCriteria &&
          procCriteria.ProcessCriteria.length
        ) {
          payload.ProcessSearchCriteria = procCriteria.ProcessCriteria;
        }

        const res = await BomBulkUpdateService.computeDiff(payload);

        // Normalise for display; store raw response so buildDiffSnapshot() can strip it
        this.diffBoms = this.normaliseDiff(res.data);
        this.handleZeroAffectedBoms();

        this.$emit("diff-computed", {
          diffSnapshot: res.data,
          diffComputedAt: res.data.DiffComputedAt || new Date().toISOString(),
        });
      } catch {
        this.computeError = "Failed to compute changes. Please try again.";
      } finally {
        this.computing = false;
      }
    },

    // ─── Normalisation ─────────────────────────────────────────────────────────

    normaliseDiff(snapshot) {
      if (!snapshot) return [];

      // Primary format from API: { BomDiffs: { "BOM-001": { MaterialLines, ProcessLines, ... } } }
      const bomDiffs = snapshot.BomDiffs || snapshot.bomDiffs;
      if (bomDiffs && typeof bomDiffs === "object") {
        return Object.entries(bomDiffs).map(([bomCode, bomDiff]) => {
          const matLines = (
            bomDiff.MaterialLines ||
            bomDiff.materialLines ||
            []
          ).map((l) => ({
            ...this.normaliseDiffLine(l),
            lineType: "Material",
          }));
          const procLines = (
            bomDiff.ProcessLines ||
            bomDiff.processLines ||
            []
          ).map((l) => ({ ...this.normaliseDiffLine(l), lineType: "Process" }));
          const status = bomDiff.BomStatus || bomDiff.bomStatus || "";
          return {
            bomCode,
            productName: bomDiff.ProductName || bomDiff.productName || "",
            bomStatus: status,
            dateChanged: bomDiff.DateChanged || bomDiff.dateChanged || null,
            hasLinkedMaterialWarning:
              bomDiff.HasLinkedMaterialWarning ||
              bomDiff.hasLinkedMaterialWarning ||
              false,
            isArchived: status.toLowerCase() === "archived",
            diffLines: [...procLines, ...matLines],
          };
        });
      }

      // Fallback: flat array with combined DiffLines
      if (Array.isArray(snapshot)) {
        return snapshot.map((b) => {
          const rawLines = b.DiffLines || b.diffLines || [];
          const status = b.BomStatus || b.bomStatus || "";
          return {
            bomCode: b.BomCode || b.bomCode || "",
            productName: b.ProductName || b.productName || "",
            bomStatus: status,
            dateChanged: b.DateChanged || b.dateChanged || null,
            hasLinkedMaterialWarning:
              b.HasLinkedMaterialWarning || b.hasLinkedMaterialWarning || false,
            isArchived: status.toLowerCase() === "archived",
            diffLines: rawLines.map((l) => this.normaliseDiffLine(l)),
          };
        });
      }

      return [];
    },
    normaliseDiffLine(l) {
      const code = l.Code || l.code || "";
      const processCode = l.ProcessCode || l.processCode || null;
      let lineType = l.LineType || l.lineType || null;
      if (!lineType) {
        lineType = processCode && processCode !== code ? "Material" : "Process";
      }
      return {
        code,
        newCode: l.NewCode || l.newCode || null,
        processCode,
        description: l.Description || l.description || "",
        currentValue:
          l.CurrentValue != null
            ? l.CurrentValue
            : l.currentValue != null
            ? l.currentValue
            : null,
        newValue:
          l.NewValue != null
            ? l.NewValue
            : l.newValue != null
            ? l.newValue
            : null,
        unitOfMeasure:
          l.UnitOfMeasure || l.unitOfMeasure || l.Uom || l.uom || "",
        changeType: l.ChangeType || l.changeType || "NoChange",
        lineType,
        zeroQtyWarning: l.ZeroQtyWarning || l.zeroQtyWarning || false,
      };
    },

    // ─── Panel state ───────────────────────────────────────────────────────────

    isExcluded(bomCode) {
      return this.excludedBomIds.includes(bomCode);
    },
    onExcludeBom(bomCode) {
      this.$emit("exclude-bom", bomCode);
    },
    onIncludeBom(bomCode) {
      this.$emit("include-bom", bomCode);
    },

    // ─── Auto-exclude BOMs with 0 affected lines ───────────────────────────────

    hasAffectedLines(bom) {
      return bom.diffLines.some(
        (l) => !["NoChange", "NotApplicable"].includes(l.changeType),
      );
    },

    handleZeroAffectedBoms() {
      const zeroAffectedBoms = this.diffBoms
        .filter((bom) => !this.hasAffectedLines(bom))
        .map((bom) => bom.bomCode);

      if (zeroAffectedBoms.length > 0) {
        this.$emit("auto-exclude-zero-affected", zeroAffectedBoms);
      }
    },
  },
};
</script>

<style lang="scss">
.gc-bom-bulk-update-step-preview {
  // ── Loading ──────────────────────────────────────────────────────────────────
  .pvw-computing {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
  }

  .pvw-computing-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #6c757d;
    font-size: 14px;
  }

  .pvw-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #dee2e6;
    border-top-color: #03a9f4;
    border-radius: 50%;
    animation: pvw-spin 0.7s linear infinite;
  }

  @keyframes pvw-spin {
    to {
      transform: rotate(360deg);
    }
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  .pvw-error-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 20px;
    background: #fff8f8;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    // margin: 12px 0;
  }

  .pvw-error-msg {
    font-size: 13px;
    color: #842029;
  }

  // ── Layout ───────────────────────────────────────────────────────────────────
  .pvw-layout {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .pvw-main {
    flex: 1;
    min-width: 0;
  }

  .pvw-sidebar {
    width: 240px;
    flex-shrink: 0;
  }

  // ── Main card ────────────────────────────────────────────────────────────────
  .pvw-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .pvw-card-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
  }

  .pvw-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
  }

  .pvw-card-body {
    padding: 16px;
  }

  // ── Execution order note ──────────────────────────────────────────────────────
  .pvw-exec-note {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #e8f4fd;
    border: 1px solid #b8ddf7;
    border-radius: 5px;
    font-size: 12px;
    color: #0c5e8b;
    margin-bottom: 14px;
  }

  // ── Filter pills ─────────────────────────────────────────────────────────────
  .pvw-fpills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;
  }

  .pvw-fpill {
    padding: 4px 12px;
    border: 1px solid #dee2e6;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s, border-color 0.12s, color 0.12s;

    &:hover {
      border-color: #03a9f4;
      color: #0369a1;
    }

    &.active {
      background: #03a9f4;
      border-color: #03a9f4;
      color: #fff;
    }
  }

  // ── Legend ───────────────────────────────────────────────────────────────────
  .pvw-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 12px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 5px;
    margin-bottom: 16px;
  }

  .pvw-leg-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #495057;
  }

  .pvw-leg-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
  }

  .pvw-dot-amber {
    background: #fef0c7;
    border: 1px solid #f79009;
  }

  .pvw-dot-green {
    background: #d1fadf;
    border: 1px solid #17b26a;
  }

  .pvw-dot-red {
    background: #fee4e2;
    border: 1px solid #f97066;
  }

  .pvw-dot-grey {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
  }

  // ── Type badges ───────────────────────────────────────────────────────────────
  .pvw-type-badge {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .pvw-badge-mat {
    background: #e0f2fe;
    color: #0369a1;
    border: 1px solid #b3e5fc;
  }

  .pvw-badge-proc {
    background: #ede8fb;
    color: #6a4cb0;
    border: 1px solid #c8b8f0;
  }

  // ── Diff panels list ──────────────────────────────────────────────────────────
  .pvw-diff-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  // ── Empty / footer ─────────────────────────────────────────────────────────────
  .pvw-empty {
    padding: 20px;
    text-align: center;
    color: #adb5bd;
    font-size: 13px;
  }

  .pvw-count-footer {
    margin-top: 12px;
    font-size: 12px;
    color: #6c757d;
  }

  // ── Sidebar summary card ───────────────────────────────────────────────────────
  .pvw-sum-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .pvw-sum-header {
    padding: 10px 14px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;
    border-radius: 6px 6px 0 0;
  }

  .pvw-sum-title {
    font-size: 12px;
    font-weight: 600;
    color: #344054;
  }

  .pvw-sum-body {
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .pvw-sum-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pvw-sum-lbl {
    font-size: 12px;
    color: #495057;
  }

  .pvw-sum-val {
    font-size: 13px;
    font-weight: 600;
    color: #101828;
  }

  .pvw-sv-blue {
    color: #0369a1;
  }

  .pvw-sv-red {
    color: #dc3545;
  }

  .pvw-sv-amber {
    color: #92400e;
  }

  .pvw-sv-green {
    color: #065f46;
  }
}
</style>
